import React, { useState, useEffect } from 'react';
import { Table, Button, Alert, Spinner } from 'react-bootstrap';
import { Download, Info, Trash2 } from 'lucide-react';
import { API_ENDPOINTS } from '../../../config/api.config';
import { apiService } from '../../../services/apiService';
import DeleteModal from '../../modals/data-source/DeleteModal';
import DetailsModal from '../../modals/data-source/DetailsModal';
import { File, SyncedDriveFilesProps, SyncedFile } from '../../../types/knowledge-base';

interface SyncedFilesResponse {
    dataSources: SyncedFile[];
}

export const SyncedDriveFiles: React.FC<SyncedDriveFilesProps> = ({
                                                                      onError,
                                                                      onSuccess,
                                                                      isAdmin = false
                                                                  }) => {
    const [files, setFiles] = useState<SyncedFile[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState<SyncedFile | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const fetchSyncedFiles = async () => {
        try {
            const data = await apiService.get<SyncedFilesResponse>(API_ENDPOINTS.DRIVE.SYNCED);
            setFiles(data.dataSources || []);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch synced files';
            setError(errorMessage);
            onError?.(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void fetchSyncedFiles();
    }, []);

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleDelete = async () => {
        if (!selectedFile) return;

        setIsDeleting(true);
        setDeleteError(null);

        try {
            await apiService.delete(`${API_ENDPOINTS.FILES.DELETE}/${selectedFile.id}`);
            await fetchSyncedFiles();
            onSuccess?.(`${selectedFile.metadata.original_filename} deleted successfully`);
            setShowDeleteModal(false);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete file';
            setDeleteError(errorMessage);
            onError?.(errorMessage);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDownload = async (file: SyncedFile) => {
        try {
            await apiService.downloadFile(
                `${API_ENDPOINTS.FILES.DOWNLOAD}/${file.id}`,
                file.metadata.original_filename
            );
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to download file';
            onError?.(errorMessage);
        }
    };

    const convertToFile = (syncedFile: SyncedFile): File => ({
        id: syncedFile.id,
        name: syncedFile.metadata.original_filename,
        fileSize: syncedFile.metadata.file_size,
        mimeType: syncedFile.metadata.mime_type,
        created_at: syncedFile.created_at,
        uploadedBy: syncedFile.created_by_name,
        status: syncedFile.status,
        isOwner: false,
        canManage: isAdmin,
        file_path: syncedFile.metadata.object_name || '',
        groups: [],
        groupAccess: []
    });

    if (isLoading) {
        return (
            <div className="text-center p-4">
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    return (
        <div className="synced-files">
            <h6 className="mb-3">Synced Drive Files</h6>

            <Table hover responsive>
                <thead>
                <tr>
                    <th>File Name</th>
                    <th>Size</th>
                    <th>Status</th>
                    <th>Synced At</th>
                    <th>Actions</th>
                </tr>
                </thead>
                <tbody>
                {files.length === 0 ? (
                    <tr>
                        <td colSpan={5} className="text-center py-4">
                            No synced files found
                        </td>
                    </tr>
                ) : (
                    files.map(file => (
                        <tr key={file.id}>
                            <td>{file.metadata.original_filename}</td>
                            <td>{formatFileSize(file.metadata.file_size)}</td>
                            <td>
                                    <span className={`badge bg-${file.status === 'active' ? 'success' :
                                        file.status === 'pending' ? 'warning' : 'danger'}`}>
                                        {file.status.charAt(0).toUpperCase() + file.status.slice(1)}
                                    </span>
                            </td>
                            <td>
                                {new Date(file.metadata.sync_completed_at || file.created_at).toLocaleString()}
                            </td>
                            <td>
                                <Button
                                    variant="link"
                                    className="p-0 me-2"
                                    onClick={() => {
                                        setSelectedFile(file);
                                        setShowDetailsModal(true);
                                    }}
                                    title="Details"
                                >
                                    <Info size={18} />
                                </Button>

                                <Button
                                    variant="link"
                                    className="p-0 me-2"
                                    onClick={() => void handleDownload(file)}
                                    disabled={file.status !== 'active'}
                                    title="Download"
                                >
                                    <Download size={18} />
                                </Button>

                                {isAdmin && (
                                    <Button
                                        variant="link"
                                        className="p-0 text-danger"
                                        onClick={() => {
                                            setSelectedFile(file);
                                            setShowDeleteModal(true);
                                        }}
                                        title="Delete"
                                    >
                                        <Trash2 size={18} />
                                    </Button>
                                )}
                            </td>
                        </tr>
                    ))
                )}
                </tbody>
            </Table>

            <DeleteModal
                show={showDeleteModal}
                fileName={selectedFile?.metadata.original_filename || ''}
                isDeleting={isDeleting}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                error={deleteError}
            />

            {selectedFile && (
                <DetailsModal
                    show={showDetailsModal}
                    onHide={() => setShowDetailsModal(false)}
                    file={convertToFile(selectedFile)}
                    isAdmin={isAdmin}
                    formatFileSize={formatFileSize}
                />
            )}
        </div>
    );
};

export default SyncedDriveFiles;